import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getOrders, updateOrder, deleteOrder } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Search, ShoppingCart, MoreVertical, Eye, Trash2, Ticket, UserCog } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { ja } from 'date-fns/locale';

const statusConfig = {
  pending: { label: '受付中', className: 'bg-amber-100 text-amber-800' },
  confirmed: { label: '確定', className: 'bg-blue-100 text-blue-800' },
  shipped: { label: '発送済', className: 'bg-purple-100 text-purple-800' },
  completed: { label: '完了', className: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'キャンセル', className: 'bg-red-100 text-red-800' },
};

// 配送日ごとの背景色
const deliveryDateColors = [
  'bg-blue-50',
  'bg-green-50',
  'bg-amber-50',
  'bg-purple-50',
  'bg-pink-50',
  'bg-cyan-50',
  'bg-orange-50',
];

export const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await getOrders();
      setOrders(res.data);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch = o.order_number.toLowerCase().includes(searchTerm.toLowerCase()) || o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || o.status === statusFilter;
    const dd = o.delivery_date || '';
    const matchesDateFrom = !dateFrom || dd >= dateFrom;
    const matchesDateTo = !dateTo || dd <= dateTo;
    return matchesSearch && matchesStatus && matchesDateFrom && matchesDateTo;
  });

  // 配送日ごとにグループ化して色を割り当て
  const deliveryDates = [...new Set(filteredOrders.map(o => o.delivery_date || 'なし'))].sort();
  const dateColorMap = {};
  deliveryDates.forEach((date, idx) => {
    dateColorMap[date] = deliveryDateColors[idx % deliveryDateColors.length];
  });

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrder(orderId, { status: newStatus });
      toast.success('ステータスを更新しました');
      fetchData();
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  const handleDelete = (order) => {
    setSelectedOrder(order);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteOrder(selectedOrder.id);
      toast.success('注文を削除しました');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('削除に失敗しました');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="orders-page">
      <div className="page-header">
        <h1 className="page-title">注文管理</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="注文番号・取引先で検索..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">すべて</SelectItem>
            <SelectItem value="pending">受付中</SelectItem>
            <SelectItem value="confirmed">確定</SelectItem>
            <SelectItem value="shipped">発送済</SelectItem>
            <SelectItem value="completed">完了</SelectItem>
            <SelectItem value="cancelled">キャンセル</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">配送日:</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" />
          <span className="text-slate-400">〜</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" placeholder="指定なし" />
        </div>
      </div>

      {/* 配送日の凡例 */}
      {deliveryDates.length > 1 && (
        <div className="flex flex-wrap gap-2 text-sm">
          <span className="text-slate-500">配送日:</span>
          {deliveryDates.map((date) => (
            <span key={date} className={`px-2 py-1 rounded ${dateColorMap[date]}`}>
              {date === 'なし' ? '未設定' : format(new Date(date), 'MM/dd(E)', { locale: ja })}
            </span>
          ))}
        </div>
      )}

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <ShoppingCart className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>注文がありません</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-16">No.</th>
                    <th>取引先</th>
                    <th>商品数</th>
                    <th>合計金額</th>
                    <th>クーポン</th>
                    <th>配送日</th>
                    <th>ステータス</th>
                    <th>注文日</th>
                    <th className="text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => {
                    const status = statusConfig[order.status] || statusConfig.pending;
                    const deliveryKey = order.delivery_date || 'なし';
                    const rowBgColor = dateColorMap[deliveryKey] || '';
                    return (
                      <tr key={order.id} className={rowBgColor}>
                        <td className="w-16">
                          <Link to={`/orders/${order.id}`} className="text-xs text-blue-600 hover:underline truncate block max-w-[60px]" title={order.order_number}>
                            {order.order_number.replace('ORD-', '').slice(-6)}
                          </Link>
                        </td>
                        <td className="font-medium">
                          <div className="flex items-center gap-2">
                            <span>{order.customer_name || '-'}</span>
                            {order.is_proxy_order && (
                              <Badge className="bg-orange-100 text-orange-700 text-xs px-1.5 py-0">
                                <UserCog className="h-3 w-3 mr-0.5" />
                                代理
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td>{order.items?.length || 0}点</td>
                        <td className="font-mono font-medium">¥{order.total_amount?.toLocaleString()}</td>
                        <td>
                          {order.coupon_name ? (
                            <div className="flex items-center gap-1">
                              <Ticket className="h-3 w-3 text-purple-600" />
                              <span className="text-sm text-purple-700">{order.coupon_name}</span>
                              {order.coupon_discount > 0 && (
                                <Badge variant="secondary" className="text-xs ml-1">
                                  -¥{order.coupon_discount?.toLocaleString()}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td>
                          {order.delivery_date ? (
                            <span className="font-medium">
                              {format(new Date(order.delivery_date), 'MM/dd(E)', { locale: ja })}
                            </span>
                          ) : '-'}
                        </td>
                        <td>
                          <Select value={order.status} onValueChange={(v) => handleStatusChange(order.id, v)}>
                            <SelectTrigger className="w-28 h-8">
                              <Badge className={status.className}>{status.label}</Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">受付中</SelectItem>
                              <SelectItem value="confirmed">確定</SelectItem>
                              <SelectItem value="shipped">発送済</SelectItem>
                              <SelectItem value="completed">完了</SelectItem>
                              <SelectItem value="cancelled">キャンセル</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="text-slate-500">{format(new Date(order.created_at), 'yyyy/MM/dd', { locale: ja })}</td>
                        <td className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/orders/${order.id}`}><Eye className="h-4 w-4 mr-2" />詳細を見る</Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(order)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />削除
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>注文を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>「{selectedOrder?.order_number}」を削除します。この操作は取り消せません。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">削除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
